import { ChangeDetectorRef, Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { AbstractControl, FormArray, FormControl, FormGroup, ValidationErrors, Validators } from '@angular/forms';
import { Observable, firstValueFrom } from 'rxjs';

import { FormValidators } from '../../../../../core/forms/form-validators';
import { SmartForm } from '../../../../../core/forms/smart-form';
import { SmartFormService } from '../../../../../core/forms/smart-form.service';
import { AnalyticDistributionModelRecord } from '../../../../../core/services/analytic-distribution';
import { AutocompleteOption } from '../../../../../shared/inputs/autocomplete-select/autocomplete-select.component';
import { AnalyticDistributionModelApi } from '../analytic-distribution-model-api';

interface OwnerOption {
  id: string;
  label: string;
  code?: string | null;
}

interface DistributionOptions {
  analytic_accounts: OwnerOption[];
  partners: OwnerOption[];
  products: OwnerOption[];
  product_categories: OwnerOption[];
}

interface DistributionLine {
  analytic_account_id: string;
  percentage: number;
}

export interface AnalyticDistributionModelContext {
  partnerId?: string | null;
  productId?: string | null;
  productCategoryId?: string | null;
  accountCode?: string | null;
}

@Component({
  selector: 'app-analytic-distribution-model-new-modal',
  standalone: false,
  templateUrl: './new-modal.html',
  styleUrl: './new-modal.scss',
})
export class AnalyticDistributionModelNewModal implements OnChanges {
  @Input() open = false;
  @Input() context: AnalyticDistributionModelContext = {};
  @Input() initialDistribution: Record<string, number> = {};
  @Output() openChange = new EventEmitter<boolean>();
  @Output() saved = new EventEmitter<AnalyticDistributionModelRecord>();

  form: SmartForm<Record<string, unknown>>;
  loading = false;
  errorMessage = '';
  options: DistributionOptions = { analytic_accounts: [], partners: [], products: [], product_categories: [] };
  private optionsLoaded = false;

  readonly searchPartners = (query: string) => this.searchOptions('partners', query);
  readonly preloadPartners = (ids: string[]) => this.preloadOptions('partners', ids);
  readonly searchProducts = (query: string) => this.searchOptions('products', query);
  readonly preloadProducts = (ids: string[]) => this.preloadOptions('products', ids);
  readonly searchCategories = (query: string) => this.searchOptions('product_categories', query);
  readonly preloadCategories = (ids: string[]) => this.preloadOptions('product_categories', ids);

  constructor(
    private readonly api: AnalyticDistributionModelApi,
    smartForms: SmartFormService,
    private readonly cdr: ChangeDetectorRef,
  ) {
    this.form = smartForms.create<Record<string, unknown>>(
      {
        name: ['', [FormValidators.required('model name')]],
        partner_id: [''],
        product_id: [''],
        product_category_id: [''],
        account_prefix: [''],
        sequence: [10, [Validators.required, Validators.min(0)]],
        active: [true],
        lines: new FormArray([], [this.distributionTotalValidator]),
      },
      (value) => this.saveModel(value),
      (record: AnalyticDistributionModelRecord) => {
        this.saved.emit(record);
        this.close();
      },
      () => {
        this.errorMessage = 'Could not create the analytic distribution model.';
        this.cdr.detectChanges();
      },
      {
        successMessage: 'Analytic distribution model created.',
        invalidMessage: 'Fix the matching conditions and make sure the distribution totals 100%.',
      },
    );
  }

  async ngOnChanges(changes: SimpleChanges): Promise<void> {
    if (!changes['open'] || !this.open) return;
    this.resetForm();
    if (this.optionsLoaded) return;
    this.loading = true;
    try {
      this.options = await firstValueFrom(this.api.options<DistributionOptions>());
      this.optionsLoaded = true;
    } catch {
      this.errorMessage = 'Model options could not be loaded.';
    } finally {
      this.loading = false;
      this.cdr.detectChanges();
    }
  }

  get lines(): FormArray {
    return this.form.form.get('lines') as FormArray;
  }

  get percentageTotal(): number {
    return this.lines.controls.reduce((total, line) => total + Number(line.get('percentage')?.value || 0), 0);
  }

  addLine(line?: Partial<DistributionLine>): void {
    this.lines.push(this.createLine(line));
    this.lines.markAsDirty();
    this.lines.updateValueAndValidity();
  }

  removeLine(index: number): void {
    this.lines.removeAt(index);
    if (this.lines.length === 0) this.addLine({ percentage: 100 });
    this.lines.markAsDirty();
    this.lines.updateValueAndValidity();
  }

  submit(): void {
    this.lines.updateValueAndValidity();
    this.form.submit();
  }

  close(): void {
    if (this.form.pending) return;
    this.openChange.emit(false);
  }

  private resetForm(): void {
    this.errorMessage = '';
    this.form.form.reset({
      name: this.defaultName(),
      partner_id: this.context.partnerId ?? '',
      product_id: this.context.productId ?? '',
      product_category_id: this.context.productCategoryId ?? '',
      account_prefix: this.context.accountCode ?? '',
      sequence: 10,
      active: true,
    });
    this.lines.clear();
    const initialLines = Object.entries(this.initialDistribution ?? {});
    if (initialLines.length) {
      initialLines.forEach(([analyticAccountId, percentage]) => this.addLine({
        analytic_account_id: analyticAccountId,
        percentage: Number(percentage),
      }));
    } else {
      this.addLine({ percentage: 100 });
    }
    this.form.form.markAsPristine();
  }

  private defaultName(): string {
    if (this.context.productId) return 'Product analytic distribution';
    if (this.context.partnerId) return 'Partner analytic distribution';
    if (this.context.accountCode) return `Account ${this.context.accountCode} analytic distribution`;
    return 'Automatic analytic distribution';
  }

  private createLine(line?: Partial<DistributionLine>): FormGroup {
    return new FormGroup({
      analytic_account_id: new FormControl(line?.analytic_account_id ?? '', [Validators.required]),
      percentage: new FormControl(Number(line?.percentage ?? 0), [Validators.required, Validators.min(0.01), Validators.max(100)]),
    });
  }

  private saveModel(value: Record<string, unknown>): Observable<AnalyticDistributionModelRecord> {
    return this.api.create<AnalyticDistributionModelRecord>({
      name: String(value['name'] ?? '').trim(),
      partner_id: this.optionalText(value['partner_id']),
      product_id: this.optionalText(value['product_id']),
      product_category_id: this.optionalText(value['product_category_id']),
      account_prefix: this.optionalText(value['account_prefix']),
      sequence: Number(value['sequence'] ?? 10),
      active: Boolean(value['active']),
      lines: (value['lines'] as DistributionLine[]).map((line) => ({
        analytic_account_id: line.analytic_account_id,
        percentage: Number(line.percentage),
      })),
    });
  }

  private readonly distributionTotalValidator = (control: AbstractControl): ValidationErrors | null => {
    const lines = (control.value ?? []) as DistributionLine[];
    const total = lines.reduce((sum, line) => sum + Number(line.percentage || 0), 0);
    return lines.length > 0 && Math.abs(total - 100) < 0.0001
      ? null
      : { distributionTotal: { message: 'Distribution percentages must total exactly 100%.' } };
  };

  private searchOptions(key: keyof DistributionOptions, query: string): Promise<AutocompleteOption[]> {
    const normalized = query.trim().toLowerCase();
    return Promise.resolve(this.options[key]
      .filter((option) => !normalized || `${option.code ?? ''} ${option.label}`.toLowerCase().includes(normalized))
      .map((option) => ({ value: option.id, label: option.code ? `${option.code} · ${option.label}` : option.label })));
  }

  private preloadOptions(key: keyof DistributionOptions, ids: string[]): Promise<AutocompleteOption[]> {
    const wanted = new Set(ids);
    return Promise.resolve(this.options[key]
      .filter((option) => wanted.has(option.id))
      .map((option) => ({ value: option.id, label: option.code ? `${option.code} · ${option.label}` : option.label })));
  }

  private optionalText(value: unknown): string | null {
    const normalized = String(value ?? '').trim();
    return normalized || null;
  }
}
